/* global BigInt */

// Presentation only. Positions are local to siblings; reads never renumber.
const position = (item) => {
  const value = item.orderIndex ?? item.order_index;
  return (typeof value === "number" || (typeof value === "string" && value.trim() !== ""))
    && Number.isSafeInteger(Number(value)) ? Number(value) : null;
};
const orderedItems = (items = []) => items.map((item, index) => ({ item, index, order: position(item) }))
  .sort((a, b) => {
    if ((a.order === null) !== (b.order === null)) return a.order === null ? 1 : -1;
    if (a.order !== b.order) return a.order - b.order;
    const left = a.item.id ?? a.item.clientId, right = b.item.id ?? b.item.clientId;
    if (left != null && right != null && String(left) !== String(right)) {
      if (/^\d+$/.test(String(left)) && /^\d+$/.test(String(right)) && BigInt(left) !== BigInt(right)) return BigInt(left) < BigInt(right) ? -1 : 1;
      return String(left) < String(right) ? -1 : 1;
    }
    return a.index - b.index;
  }).map(({ item }) => item);

const invalid = () => { throw Object.assign(new Error("Hiérarchie du devis invalide : vérifiez les identifiants et les parents."),
  { status: 409, code: "QUOTE_STRUCTURE_INVALID" }); };
const orderedSections = (sections = [], fail = invalid) => {
  const nodes = new Map(), siblings = new Map();
  for (const section of sections) {
    if (section.id == null || nodes.has(String(section.id))) fail();
    nodes.set(String(section.id), section);
  }
  for (const section of sections) {
    const parent = section.parentSectionId ?? section.parent_section_id;
    const path = new Set([String(section.id)]);
    let cursor = parent;
    while (cursor != null) {
      if (path.has(String(cursor)) || !nodes.has(String(cursor))) fail();
      path.add(String(cursor));
      const ancestor = nodes.get(String(cursor));
      if (section.quoteVersionId != null && ancestor.quoteVersionId != null
        && String(section.quoteVersionId) !== String(ancestor.quoteVersionId)) fail();
      cursor = ancestor.parentSectionId ?? ancestor.parent_section_id;
    }
    const key = parent == null ? null : String(parent);
    if (!siblings.has(key)) siblings.set(key, []);
    siblings.get(key).push(section);
  }
  const walk = (parent) => orderedItems(siblings.get(parent) || []).flatMap((section) => [section, ...walk(String(section.id))]);
  return walk(null);
};

// Whitelist of existing Python ChapterResult amounts; no JS aggregation.
const chapterTotals = (chapters = []) => {
  const totals = chapters.flatMap((chapter) => [
    { id: chapter.chapter_id, totalHT: chapter.net_selling_price_ht }, ...chapterTotals(chapter.chapters || []),
  ]);
  if (new Set(totals.map((item) => String(item.id))).size !== totals.length
    || totals.some((item) => item.id == null || (item.totalHT != null
      && (!(typeof item.totalHT === "number" || typeof item.totalHT === "string")
        || item.totalHT === "" || !Number.isFinite(Number(item.totalHT)))))) {
    throw Object.assign(new Error("Sous-totaux moteur incohérents."), { status: 422, code: "INCONSISTENT_FINANCIAL_SOURCE" });
  }
  return totals;
};
const sectionTotals = (calculated = {}) => calculated.sectionTotals
  ?? chapterTotals(calculated.calculation?.chapters || []);

const orderChapterResults = (chapters = [], sources = []) => {
  const sections = orderedSections(sources);
  const sectionRank = new Map(sections.map((section, index) => [String(section.id), index]));
  const lineRank = new Map(sections.flatMap((section) => orderedItems(section.lines || [])
    .map((line, index) => [String(line.id), index])));
  const byRank = (items, rank, key) => [...items].sort((a, b) =>
    (rank.get(String(a[key])) ?? Infinity) - (rank.get(String(b[key])) ?? Infinity));
  const walk = (items) => byRank(items, sectionRank, "chapter_id").map((chapter) => ({ ...chapter,
    lines: byRank(chapter.lines || [], lineRank, "line_id"), chapters: walk(chapter.chapters || []),
  }));
  return walk(chapters);
};

// Only the effect's comparison key is canonicalized. The submitted financial
// request is untouched, including all monetary inputs and allocation rules.
const calculationRequestKey = (request) => {
  const byId = (items) => [...(items || [])].sort((a, b) => String(a.id) < String(b.id) ? -1 : String(a.id) > String(b.id) ? 1 : 0);
  const chapters = (items) => byId(items).map((chapter) => ({ ...chapter, lines: byId(chapter.lines), chapters: chapters(chapter.chapters) }));
  if (!request?.quote) return JSON.stringify(request);
  return JSON.stringify({ ...request, quote: { ...request.quote, chapters: chapters(request.quote.chapters),
    options: byId(request.quote.options).map((option) => ({ ...option, chapters: chapters(option.chapters) })),
  } });
};

module.exports = { orderedItems, orderedSections, chapterTotals, sectionTotals, orderChapterResults, calculationRequestKey };
